import React from "react";
import { useAdminContext } from "../provider/AdminContext";

const AdminDashboard = () => {
  const { companies, openCompany } = useAdminContext();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status, isActive) => {
    if (status === "active" || isActive) {
      return "bg-green-100 text-green-800";
    }
    return "bg-red-100 text-red-800";
  };

  const daysUntil = (dateString) => {
    if (!dateString) return null;
    const diffMs = new Date(dateString).getTime() - Date.now();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const getPlanColor = (plan) => {
    switch (plan?.toLowerCase()) {
      case "free":
        return "bg-blue-100 text-blue-800";
      case "premium":
        return "bg-purple-100 text-purple-800";
      case "enterprise":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (companies.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent border-solid rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className=" bg-gray-50 p-3 sm:p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Company Subscription Dashboard
          </h1>

          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
            {companies.map((company) => (
              <div
                key={company._id}
                onClick={() => openCompany(company)}
                className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
              >
                {/* Company Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">
                      {company.companyName}
                    </h2>
                    <p className="text-sm text-gray-500">
                      Created: {formatDate(company.createdAt)}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Created by:
                      {company.superAdmin
                        ? ` ${company.superAdmin.firstName} ${company.superAdmin.lastName} (${company.superAdmin.email})`
                        : " N/A"}
                    </p>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${getPlanColor(
                        company.subscription?.plan
                      )}`}
                    >
                      {company.subscription?.plan?.toUpperCase() ?? "UNKNOWN"}
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        company.subscription?.status,
                        company.subscription?.isActive
                      )}`}
                    >
                      {company.subscription?.status?.toUpperCase() ||
                        (company.subscription?.isActive
                          ? "ACTIVE"
                          : "INACTIVE")}
                    </span>
                  </div>
                </div>

                {/* Usage Statistics */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Usage Statistics
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {company.subscription?.usage?.usersCount ?? 0}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">Users</div>
                    </div>

                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {company.subscription?.usage?.executionCount ?? 0}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Executions
                      </div>
                    </div>

                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {company.subscription?.usage?.savedMappings ?? 0}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">Mappings</div>
                    </div>
                  </div>
                </div>

                {/* Subscription Details */}
                {company.subscription && (
                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      Subscription Details
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Plan:</span>
                        <span className="font-medium capitalize">
                          {company.subscription.plan ?? "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className="font-medium capitalize">
                          {company.subscription.status ?? "N/A"}
                        </span>
                      </div>

                      {company.subscription.status === "active" && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Started At:</span>
                            <span className="font-medium">
                              {company.subscription.startedAt
                                ? formatDate(company.subscription.startedAt)
                                : "N/A"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Expires At:</span>
                            <span className="font-medium">
                              {company.subscription.expiresAt
                                ? formatDate(company.subscription.expiresAt)
                                : "N/A"}
                            </span>
                          </div>
                          {company.subscription.expiresAt &&
                            (() => {
                              const daysLeft = daysUntil(
                                company.subscription.expiresAt
                              );
                              return (
                                <div className="flex justify-between col-span-2">
                                  <span className="text-gray-600">
                                    Renewal:
                                  </span>
                                  <span
                                    className={`font-medium ${
                                      daysLeft <= 7
                                        ? "text-red-600"
                                        : daysLeft <= 30
                                        ? "text-orange-600"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {daysLeft >= 0
                                      ? `${daysLeft} day${
                                          daysLeft === 1 ? "" : "s"
                                        } remaining`
                                      : `Expired ${Math.abs(daysLeft)} day${
                                          Math.abs(daysLeft) === 1 ? "" : "s"
                                        } ago`}
                                  </span>
                                </div>
                              );
                            })()}
                        </>
                      )}
                    </div>

                    {/* Plan Limits */}
                    {company.subscription?.limits && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="text-md font-medium text-gray-900 mb-3">
                          Plan Limits
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Max Users:</span>
                            <span className="font-medium">
                              {company.subscription.limits.maxUsers ??
                                "Unlimited"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Max Executions/Month:
                            </span>
                            <span className="font-medium">
                              {company.subscription.limits
                                .maxExecutionsPerMonth ?? "Unlimited"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Max Saved Mappings:
                            </span>
                            <span className="font-medium">
                              {company.subscription.limits.maxSavedMappings ??
                                "Unlimited"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Can Save Mappings:
                            </span>
                            <span className="font-medium">
                              {company.subscription.limits.canSaveMappings
                                ? "Yes"
                                : "No"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
